import { useCallback, useEffect, useRef, useState } from 'react';
import { isSearchableQuery, normalizeSearchLanguage, normalizeSearchQuery, searchPublic } from '../services/search';

const emptyState = (key, status = 'idle') => ({
  key, items: [], status, error: null, hasMore: false, loadingMore: false,
});
const cache = new Map();
const cached = (key) => {
  const entry = cache.get(key);
  return entry && entry.expiresAt > Date.now() ? entry : null;
};
// The cache contains only public results and is intentionally short lived.
export const clearSearchCache = () => cache.clear();
const initialState = (key, enabled) => {
  const entry = enabled && cached(key);
  return entry ? { ...emptyState(key, 'ready'), items: entry.items, hasMore: Boolean(entry.nextCursor) }
    : emptyState(key, enabled ? 'loading' : 'idle');
};

export default function useSearch({ kind = 'stories', query = '', language = 'en', composing = false }) {
  const text = normalizeSearchQuery(query, kind);
  const lng = normalizeSearchLanguage(language);
  const enabled = !composing && ['stories', 'users'].includes(kind) && isSearchableQuery(query, kind);
  const key = JSON.stringify([kind, text, lng, enabled]);
  const [state, setState] = useState(() => initialState(key, enabled));
  const session = useRef(null);

  useEffect(() => {
    const entry = enabled && cached(key);
    const run = {
      key, active: true, fetching: false, controller: null, request: 0,
      nextCursor: entry?.nextCursor || null, failedCursor: null, cursors: new Set(entry?.cursors || []),
      state: initialState(key, enabled),
    };
    session.current = run;
    const publish = (changes) => {
      if (!run.active) return;
      run.state = { ...run.state, ...changes };
      setState(run.state);
    };
    setState(run.state);
    run.fetchPage = async (cursor = null) => {
      if (!run.active || !enabled || run.fetching) return;
      run.fetching = true;
      const request = ++run.request;
      const controller = new AbortController();
      run.controller = controller;
      const current = () => run.active && run.request === request;
      publish({ error: null, loadingMore: Boolean(cursor), status: cursor ? 'ready' : 'loading' });
      try {
        const page = await searchPublic({
          kind, query: kind === 'users' ? `@${text}` : text, language: lng, cursor, signal: controller.signal,
        });
        if (!current()) return;
        if (cursor) run.cursors.add(cursor);
        const items = cursor ? [...run.state.items] : [];
        const seen = new Set(items.map((item) => item.id));
        for (const item of page.items) {
          if (!seen.has(item.id)) { seen.add(item.id); items.push(item); }
        }
        // A repeated cursor must not create an infinite load-more loop.
        run.nextCursor = page.hasMore && page.nextCursor && !run.cursors.has(page.nextCursor)
          ? page.nextCursor : null;
        run.failedCursor = null;
        publish({ items, status: 'ready', hasMore: Boolean(run.nextCursor), loadingMore: false });
        cache.delete(key);
        cache.set(key, { items, nextCursor: run.nextCursor, cursors: [...run.cursors], expiresAt: Date.now() + 60000 });
        while (cache.size > 5) cache.delete(cache.keys().next().value);
      } catch (error) {
        if (!current()) return;
        run.failedCursor = cursor;
        publish({ error, status: cursor ? 'ready' : 'error', loadingMore: false });
      } finally {
        if (current()) run.fetching = false;
      }
    };
    const timer = enabled && !entry ? setTimeout(() => run.fetchPage(), 300) : null;
    return () => {
      run.active = false;
      clearTimeout(timer);
      run.controller?.abort();
    };
  }, [key, enabled, kind, text, lng]);

  const loadMore = useCallback(() => {
    const run = session.current;
    if (run?.key === key && run.nextCursor) run.fetchPage(run.nextCursor);
  }, [key]);
  const retry = useCallback(() => {
    const run = session.current;
    if (run?.key === key && run.state.error) run.fetchPage(run.failedCursor);
  }, [key]);

  // Hide old results in the render where the query changes, before the effect
  // cleanup has had a chance to abort its previous request.
  const visible = state.key === key ? state : initialState(key, enabled);
  return { ...visible, loadMore, retry };
}
